import { useMemo } from 'react';
import { clsx } from 'clsx';

import { Icons } from '@monoimin/atoms';
import { useLocale, RealEstateDict } from '@monoimin/intl';

import {
  createCountriesForFilter,
  updateFiltersState,
  collectParamsFilterValues,
  defineSelectedValueTitle,
  filterCountriesByEstatesCount,
} from '../../utils/filters';

import { ESTATE_FILTER_KEYS } from '../../constants';

import { FiltersState, SelectItem } from '../../types';

import { SearchList } from '../SearchList';
import { SelectWithSearch } from '../SelectWithSearch';

import type { StrapiCountry } from '@monoimin/sb-types';

export type SpecialFilterProps = {
  filtersState: FiltersState | undefined;
  onChange?: (filtersState: FiltersState) => void;
  classNames?: {
    container?: string;
    stubTitle?: string;
    stubIcon?: string;
  };
  variant: 'desktop' | 'mobile' | 'mobile-stub';
  onClick?: () => void;
  realEstateDict: RealEstateDict;
};

type CountryFilterProps = SpecialFilterProps & {
  countries: Array<StrapiCountry>;
};

type FilterStubProps = Pick<SpecialFilterProps, 'classNames' | 'onClick'> & {
  title?: string;
};

export const FilterStub = ({ classNames, onClick, title }: FilterStubProps) => {
  return (
    <div className={classNames?.container} onClick={onClick}>
      <Icons
        icon='search'
        className={clsx('bg-grey-light', classNames?.stubIcon)}
        isInteractive
        sizeClassName='h-7'
      />
      <span
        className={clsx(
          'body whitespace-nowrap truncate',
          classNames?.stubTitle
        )}
      >
        {title}
      </span>
    </div>
  );
};

export const CountryFilter = ({
  countries,
  filtersState,
  onChange,
  classNames,
  variant,
  onClick,
  realEstateDict,
}: CountryFilterProps) => {
  const locale = useLocale();

  const filteredCountries = useMemo(() => {
    return filterCountriesByEstatesCount(countries);
  }, [countries]);

  const countriesForFilter = useMemo(() => {
    return createCountriesForFilter(filteredCountries, locale);
  }, [filteredCountries, locale]);

  const countriesValue = useMemo(() => {
    return collectParamsFilterValues(countriesForFilter, filtersState?.country);
  }, [countriesForFilter, filtersState?.country]);

  const countriesValueTitle = useMemo(() => {
    return defineSelectedValueTitle(
      countriesValue,
      realEstateDict.termOptionAll
    );
  }, [countriesValue, realEstateDict.termOptionAll]);

  const onFilterChange = (value: SelectItem | Array<SelectItem>) => {
    if (typeof onChange !== 'function') {
      return;
    }

    const newFiltersState = updateFiltersState({
      key: ESTATE_FILTER_KEYS.COUNTRY,
      selectedData: value,
      filtersState: { ...filtersState, [ESTATE_FILTER_KEYS.CITY]: [] },
    });

    onChange(newFiltersState);
  };

  if (variant === 'mobile-stub') {
    return (
      <FilterStub
        title={countriesValueTitle}
        classNames={classNames}
        onClick={onClick}
      />
    );
  }

  if (variant === 'mobile') {
    return (
      <SearchList
        data={countriesForFilter}
        value={countriesValue}
        onChange={onFilterChange}
        placeholder={realEstateDict.country}
        realEstateDict={realEstateDict}
      />
    );
  }

  return (
    <SelectWithSearch
      data={countriesForFilter}
      placeholder={realEstateDict.termOptionAll}
      value={countriesValue}
      onChange={onFilterChange}
      searchConfig={{
        placeholder: realEstateDict.findCountry,
      }}
      classNames={classNames}
      realEstateDict={realEstateDict}
      variant='column'
    />
  );
};
